'use client'

import { memo, type RefObject } from 'react'
import { useTranslation } from '@/contexts/I18nContext'
import type { NotificationListItem } from '@/lib/api/notification'
import { NotificationEmptyState } from './NotificationEmptyState'
import { NotificationItem } from './NotificationItem'

export type NotificationFeedProps = {
  variant: 'mobile' | 'desktop'
  items: NotificationListItem[]
  loading: boolean
  loadMoreRef: RefObject<HTMLDivElement | null>
  onItemClick: (item: NotificationListItem) => void
  onActorClick: (item: NotificationListItem) => void
  onNpcClick: (item: NotificationListItem) => void
}

type Props = NotificationFeedProps

function LoadingIndicator({ variant }: Pick<Props, 'variant'>) {
  const isDesktop = variant === 'desktop'
  return (
    <div
      className={
        isDesktop
          ? 'flex h-[220px] items-center justify-center'
          : 'flex justify-center pt-[calc(182*var(--vw))]'
      }
    >
      <div
        className={
          isDesktop
            ? 'size-[24px] animate-spin rounded-full border-2 border-white/20 border-t-white'
            : 'h-[calc(28*var(--vw))] w-[calc(28*var(--vw))] animate-spin rounded-full border-2 border-white/20 border-t-white'
        }
      />
    </div>
  )
}

function EmptyState({ variant }: Pick<Props, 'variant'>) {
  const { t } = useTranslation('message')
  if (variant === 'mobile') return <NotificationEmptyState />
  return (
    <div className="flex h-[220px] items-center justify-center text-[14px] text-white/40">
      {t('Message.Notification.Empty')}
    </div>
  )
}

function getListClass(variant: Props['variant']) {
  // Figma mobile: tabs end at 132, first card at 144 → 12; card stride 92 with h=80 → gap 12
  if (variant === 'desktop') {
    return 'flex flex-col gap-[12px]'
  }
  return 'flex flex-col gap-[calc(12*var(--vw))] pt-[calc(12*var(--vw))]'
}

function LoadMoreIndicator({ variant }: Pick<Props, 'variant'>) {
  if (variant === 'desktop') {
    return (
      <div className="flex justify-center py-[8px]">
        <div className="size-[18px] animate-spin rounded-full border-2 border-white/20 border-t-white" />
      </div>
    )
  }
  return (
    <div className="flex justify-center py-[calc(16*var(--vw))]">
      <div className="h-[calc(22*var(--vw))] w-[calc(22*var(--vw))] animate-spin rounded-full border-2 border-white/20 border-t-white" />
    </div>
  )
}

function NotificationFeedComponent({
  variant,
  items,
  loading,
  loadMoreRef,
  onItemClick,
  onActorClick,
  onNpcClick,
}: NotificationFeedProps) {
  if (loading && items.length === 0) return <LoadingIndicator variant={variant} />
  if (items.length === 0) return <EmptyState variant={variant} />

  return (
    <>
      <div className={getListClass(variant)}>
        {items.map((item) => (
          <NotificationItem
            key={item.id}
            variant={variant}
            item={item}
            onClick={onItemClick}
            onActorClick={onActorClick}
            onNpcClick={onNpcClick}
          />
        ))}
      </div>
      <div
        ref={loadMoreRef}
        className={variant === 'desktop' ? 'h-[1px]' : 'h-[calc(1*var(--vw))]'}
      />
      {loading && <LoadMoreIndicator variant={variant} />}
    </>
  )
}

export const NotificationFeed = memo(NotificationFeedComponent)
